import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { AppLayout } from "@/layouts/AppLayout";
import { AuthLayout } from "@/layouts/AuthLayout";
import { MarketingLayout } from "@/layouts/MarketingLayout";
import { MarketingGate } from "@/components/marketing/MarketingGate";
import { ProtectedRoute } from "@/auth/ProtectedRoute";
import { PageSkeleton } from "@/components/shared/PageSkeleton";

// Lazy-loaded marketing pages
const HomePage = lazy(() => import("@/pages/marketing/HomePage").then(m => ({ default: m.HomePage })));
const FeaturesPage = lazy(() => import("@/pages/marketing/FeaturesPage").then(m => ({ default: m.FeaturesPage })));
const PricingPage = lazy(() => import("@/pages/marketing/PricingPage").then(m => ({ default: m.PricingPage })));
const ContactPage = lazy(() => import("@/pages/marketing/ContactPage").then(m => ({ default: m.ContactPage })));
const BlogPage = lazy(() => import("@/pages/marketing/BlogPage").then(m => ({ default: m.BlogPage })));
const ChangelogPage = lazy(() => import("@/pages/marketing/ChangelogPage").then(m => ({ default: m.ChangelogPage })));
const CompliancePage = lazy(() => import("@/pages/marketing/CompliancePage").then(m => ({ default: m.CompliancePage })));

// Lazy-loaded pages — each becomes a separate chunk
const LoginPage = lazy(() => import("@/pages/LoginPage").then(m => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => import("@/pages/RegisterPage").then(m => ({ default: m.RegisterPage })));
const AcceptInvitePage = lazy(() => import("@/pages/AcceptInvitePage").then(m => ({ default: m.AcceptInvitePage })));
const OnboardingPage = lazy(() => import("@/pages/OnboardingPage").then(m => ({ default: m.OnboardingPage })));
const AdminOrgsPage = lazy(() => import("@/pages/AdminOrgsPage").then(m => ({ default: m.AdminOrgsPage })));
const DashboardPage = lazy(() => import("@/pages/DashboardPage").then(m => ({ default: m.DashboardPage })));
const StudiesPage = lazy(() => import("@/pages/StudiesPage").then(m => ({ default: m.StudiesPage })));
const UnifiedStudyPage = lazy(() => import("@/pages/UnifiedStudyPage").then(m => ({ default: m.UnifiedStudyPage })));
const SitesPage = lazy(() => import("@/pages/SitesPage").then(m => ({ default: m.SitesPage })));
const SubjectsPage = lazy(() => import("@/pages/SubjectsPage").then(m => ({ default: m.SubjectsPage })));
const QueriesPage = lazy(() => import("@/pages/QueriesPage").then(m => ({ default: m.QueriesPage })));
const ChatPage = lazy(() => import("@/pages/ChatPage").then(m => ({ default: m.ChatPage })));
const CalendarPage = lazy(() => import("@/pages/CalendarPage").then(m => ({ default: m.CalendarPage })));
const AlertsPage = lazy(() => import("@/pages/AlertsPage").then(m => ({ default: m.AlertsPage })));
const IntegrationsPage = lazy(() => import("@/pages/IntegrationsPage").then(m => ({ default: m.IntegrationsPage })));
const UsersPage = lazy(() => import("@/pages/UsersPage").then(m => ({ default: m.UsersPage })));
const SettingsPage = lazy(() => import("@/pages/SettingsPage").then(m => ({ default: m.SettingsPage })));
const AuditPage = lazy(() => import("@/pages/AuditPage").then(m => ({ default: m.AuditPage })));
const BillingPage = lazy(() => import("@/pages/BillingPage").then(m => ({ default: m.BillingPage })));
const SiteDetailPage = lazy(() => import("@/pages/SiteDetailPage").then(m => ({ default: m.SiteDetailPage })));
const SubjectDetailPage = lazy(() => import("@/pages/SubjectDetailPage").then(m => ({ default: m.SubjectDetailPage })));
const SkillsPage = lazy(() => import("@/pages/SkillsPage").then(m => ({ default: m.SkillsPage })));
const ProtocolReviewPage = lazy(() => import("@/pages/ProtocolReviewPage").then(m => ({ default: m.ProtocolReviewPage })));
const StudyBuilderPage = lazy(() => import("@/pages/StudyBuilderPage").then(m => ({ default: m.StudyBuilderPage })));
const SearchPage = lazy(() => import("@/pages/SearchPage").then(m => ({ default: m.SearchPage })));
const SiteDirectoryPage = lazy(() => import("@/pages/SiteDirectoryPage").then(m => ({ default: m.SiteDirectoryPage })));
const SiteProfileDetailPage = lazy(() => import("@/pages/SiteProfileDetailPage").then(m => ({ default: m.SiteProfileDetailPage })));
const NotificationsPage = lazy(() => import("@/pages/NotificationsPage").then(m => ({ default: m.NotificationsPage })));
const ProfilePage = lazy(() => import("@/pages/ProfilePage").then(m => ({ default: m.ProfilePage })));
const PeopleDirectoryPage = lazy(() => import("@/pages/PeopleDirectoryPage").then(m => ({ default: m.PeopleDirectoryPage })));
const KnowledgeBasePage = lazy(() => import("@/pages/KnowledgeBasePage").then(m => ({ default: m.KnowledgeBasePage })));

// Internal pages
const InternalLayout = lazy(() => import("@/layouts/InternalLayout").then(m => ({ default: m.InternalLayout })));
const InternalDashboardPage = lazy(() => import("@/pages/internal/InternalDashboardPage").then(m => ({ default: m.InternalDashboardPage })));
const InternalClientsPage = lazy(() => import("@/pages/internal/ClientsPage").then(m => ({ default: m.ClientsPage })));
const InternalAgentListPage = lazy(() => import("@/pages/internal/AgentListPage").then(m => ({ default: m.AgentListPage })));
const InternalAgentDetailPage = lazy(() => import("@/pages/internal/AgentDetailPage").then(m => ({ default: m.AgentDetailPage })));
const InternalKnowledgePage = lazy(() => import("@/pages/internal/InternalKnowledgePage").then(m => ({ default: m.InternalKnowledgePage })));
const InternalTeamPage = lazy(() => import("@/pages/internal/TeamPage").then(m => ({ default: m.TeamPage })));
const InternalKanbanPage = lazy(() => import("@/pages/internal/KanbanPage").then(m => ({ default: m.KanbanPage })));

const NotFoundPage = lazy(() => import("@/pages/NotFoundPage").then(m => ({ default: m.NotFoundPage })));

function S({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageSkeleton />}>{children}</Suspense>;
}

export const router = createBrowserRouter([
  // Marketing site (password-gated preview)
  {
    element: <MarketingGate />,
    children: [{
      element: <MarketingLayout />,
      children: [
        { index: true, path: "/", element: <S><HomePage /></S> },
        { path: "features", element: <S><FeaturesPage /></S> },
        { path: "pricing", element: <S><PricingPage /></S> },
        { path: "compliance", element: <S><CompliancePage /></S> },
        { path: "contact", element: <S><ContactPage /></S> },
        { path: "blog", element: <S><BlogPage /></S> },
        { path: "changelog", element: <S><ChangelogPage /></S> },
      ],
    }],
  },

  // Public routes (auth layout)
  {
    element: <AuthLayout />,
    children: [
      { path: "login", element: <S><LoginPage /></S> },
      { path: "register", element: <S><RegisterPage /></S> },
      { path: "accept-invite/:token", element: <S><AcceptInvitePage /></S> },
    ],
  },

  // Protected routes
  {
    element: <ProtectedRoute />,
    children: [
      // Full-screen onboarding wizard (no sidebar)
      { path: "onboarding", element: <S><OnboardingPage /></S> },

      // Standard pages (app layout with sidebar + inspector)
      {
        element: <AppLayout />,
        children: [
          { path: "dashboard", element: <S><DashboardPage /></S> },

          // ── Studies (unified) ─────────────────────────
          { path: "studies", element: <S><StudiesPage /></S> },
          { path: "studies/new", element: <S><StudyBuilderPage /></S> },
          { path: "studies/:id", element: <S><UnifiedStudyPage /></S> },
          { path: "studies/:id/:tab", element: <S><UnifiedStudyPage /></S> },
          { path: "studies/:studyId/sites/:siteId", element: <S><SiteDetailPage /></S> },
          { path: "studies/:studyId/subjects/:subjectId", element: <S><SubjectDetailPage /></S> },

          // ── Redirects from old routes ─────────────────
          { path: "studies/builds/:buildId", element: <RedirectToBuild /> },
          { path: "protocols", element: <Navigate to="/studies" replace /> },
          { path: "protocols/:protocolId/review", element: <S><ProtocolReviewPage /></S> },
          { path: "builder/new", element: <Navigate to="/studies/new" replace /> },
          { path: "builder/:buildId", element: <RedirectToBuild /> },
          { path: "builder/:buildId/:artifactType/:artifactId", element: <RedirectToBuild /> },

          // Clinical Data
          { path: "sites", element: <S><SitesPage /></S> },
          { path: "subjects", element: <S><SubjectsPage /></S> },
          { path: "queries", element: <S><QueriesPage /></S> },
          { path: "site-directory", element: <S><SiteDirectoryPage /></S> },
          { path: "site-directory/:siteProfileId", element: <S><SiteProfileDetailPage /></S> },

          // Tools
          { path: "calendar", element: <S><CalendarPage /></S> },
          { path: "alerts", element: <S><AlertsPage /></S> },
          { path: "notifications", element: <S><NotificationsPage /></S> },
          { path: "profile", element: <S><ProfilePage /></S> },
          { path: "people", element: <S><PeopleDirectoryPage /></S> },
          { path: "knowledge-base", element: <S><KnowledgeBasePage /></S> },

          // Admin
          { path: "integrations", element: <S><IntegrationsPage /></S> },
          { path: "integrations/:connectionId", element: <S><IntegrationsPage /></S> },
          { path: "users", element: <S><UsersPage /></S> },
          { path: "settings", element: <S><SettingsPage /></S> },
          { path: "billing", element: <S><BillingPage /></S> },
          { path: "audit", element: <S><AuditPage /></S> },
          { path: "skills", element: <S><SkillsPage /></S> },
          { path: "admin/organizations", element: <S><AdminOrgsPage /></S> },

          // AI Chat
          { path: "chat", element: <S><ChatPage /></S> },
          { path: "chat/:conversationId", element: <S><ChatPage /></S> },

          // Semantic Search
          { path: "search", element: <S><SearchPage /></S> },
        ],
      },
    ],
  },

  // Internal (platformAdmin only)
  {
    path: "internal",
    element: <S><InternalLayout /></S>,
    children: [
      { index: true, element: <Navigate to="/internal/dashboard" replace /> },
      { path: "dashboard", element: <S><InternalDashboardPage /></S> },
      { path: "clients", element: <S><InternalClientsPage /></S> },
      { path: "agent", element: <Navigate to="/internal/agents/study_builder" replace /> },
      { path: "agents", element: <S><InternalAgentListPage /></S> },
      { path: "agents/:agentId", element: <S><InternalAgentDetailPage /></S> },
      { path: "knowledge", element: <S><InternalKnowledgePage /></S> },
      { path: "team", element: <S><InternalTeamPage /></S> },
      { path: "kanban", element: <S><InternalKanbanPage /></S> },
    ],
  },

  // 404
  { path: "*", element: <S><NotFoundPage /></S> },
]);

// ── Redirect helper for old build routes ────────────────

import { useParams } from "react-router-dom";

function RedirectToBuild() {
  const { buildId } = useParams<{ buildId: string }>();
  return <Navigate to={`/studies/${buildId ?? ""}`} replace />;
}
